/**
 * useRecentActivity Hook
 *
 * Provides recent platform activity from the auditLogs collection.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export type ActivityType =
  | 'user_registered'
  | 'listing_created'
  | 'listing_approved'
  | 'booking_created'
  | 'booking_confirmed'
  | 'booking_cancelled'
  | 'payout_requested'
  | 'payout_completed'
  | 'review_posted'
  | 'role_change'
  | 'user_suspended'
  | 'user_unsuspended'
  | 'listing_paused'
  | 'listing_featured'
  | 'listing_deleted'
  | 'payout_rejected'
  | 'platform_config_updated'
  | 'refund_processed';

export interface Activity {
  id: string;
  type: ActivityType;
  message: string;
  userId?: string;
  userName?: string;
  listingId?: string;
  listingTitle?: string;
  bookingId?: string;
  amount?: number;
  timestamp: Date;
}

const buildActivityMessage = (doc: Record<string, any>): string => {
  const action = doc.action as string;
  const performer = doc.performerName || 'Admin';
  const target = doc.targetName || '';
  const details = doc.details || {};

  switch (action) {
    case 'role_change':
      return `${performer} changed ${target}'s role to ${details.newRole}`;
    case 'user_suspended':
      return `${performer} suspended user ${target}`;
    case 'user_unsuspended':
      return `${performer} reinstated user ${target}`;
    case 'listing_paused':
      return `${performer} paused listing "${target}"`;
    case 'listing_approved':
      return `${performer} approved listing "${target}"`;
    case 'listing_featured':
      return `${performer} ${details.featured ? 'featured' : 'unfeatured'} listing "${target}"`;
    case 'listing_deleted':
      return `${performer} deleted listing "${target}"`;
    case 'payout_completed':
      return `Payout of $${details.amount || ''} completed for ${target}`;
    case 'payout_rejected':
      return `Payout rejected for ${target}: ${details.reason || ''}`;
    case 'platform_config_updated':
      return `${performer} updated platform configuration`;
    case 'booking_cancelled':
      return `Booking ${target} was cancelled`;
    case 'refund_processed':
      return `Refund of $${details.amount || ''} processed for ${target}`;
    default:
      return `${performer} performed ${action} on ${target}`;
  }
};

export const useRecentActivity = (limit: number = 10) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'auditLogs'),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limit)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items: Activity[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          const timestamp = data.timestamp instanceof Timestamp
            ? data.timestamp.toDate()
            : new Date(data.timestamp);

          return {
            id: doc.id,
            type: data.action as ActivityType,
            message: buildActivityMessage(data),
            userId: data.targetType === 'user' ? data.targetId : data.performedBy,
            userName: data.targetType === 'user' ? data.targetName : data.performerName,
            listingId: data.targetType === 'listing' ? data.targetId : undefined,
            listingTitle: data.targetType === 'listing' ? data.targetName : undefined,
            bookingId: data.targetType === 'booking' ? data.targetId : undefined,
            amount: data.details?.amount,
            timestamp,
          };
        });
        setActivities(items);
        setIsLoading(false);
      },
      (err) => {
        console.error('Failed to fetch recent activity:', err);
        setActivities([]);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [limit]);

  return {
    activities,
    isLoading,
  };
};

export default useRecentActivity;
